import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import {
  cityArea, DEFAULT_RADIUS, LOCATION_STORAGE_KEY, parseLocationPreferences,
} from '../../../helpers/nearbyEvents';

const EventLocationContext = createContext(null);

type Props = { children: any };

export const EventLocationProvider = ({ children }: Props) => {
  const { allCmsCity } = useStaticQuery(graphql`
    query EventLocationCities {
      allCmsCity(sort: { name: ASC }) {
        nodes { slug name lieu { geoAddress { lat lon } } }
      }
    }
  `);
  const cities = allCmsCity.nodes;
  const [preferences, setPreferences] = useState({ area: null, radius: DEFAULT_RADIUS });
  const [ready, setReady] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);

  useEffect(() => {
    try {
      setPreferences(parseLocationPreferences(window.localStorage.getItem(LOCATION_STORAGE_KEY), cities));
    } catch {
      setStorageAvailable(false);
    }
    setReady(true);
    const sync = (event) => {
      if (event.key === LOCATION_STORAGE_KEY || event.key === null) {
        request.current += 1;
        setLocating(false);
        setError('');
        setPreferences(parseLocationPreferences(event.newValue, cities));
      }
    };
    window.addEventListener('storage', sync);
    return () => {
      request.current += 1;
      window.removeEventListener('storage', sync);
    };
  }, [cities]);

  useEffect(() => {
    if (!ready) return;
    try {
      window.localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({ version: 1, ...preferences }));
    } catch {
      setStorageAvailable(false);
    }
  }, [preferences, ready]);

  const chooseArea = useCallback((area) => {
    request.current += 1;
    setLocating(false);
    setError('');
    setPreferences((previous) => ({ ...previous, area }));
  }, []);
  const chooseCity = useCallback((slug) => {
    const city = cities.find((candidate) => candidate.slug === slug);
    if (city) chooseArea(cityArea(city));
  }, [cities, chooseArea]);
  const clearArea = useCallback(() => chooseArea(null), [chooseArea]);
  const setRadius = useCallback((radius) => setPreferences((previous) => ({ ...previous, radius })), []);

  const locate = useCallback(() => {
    setError('');
    if (!navigator.geolocation) {
      setError('La localisation est indisponible dans ce navigateur. Choisissez une ville.');
      return;
    }
    request.current += 1;
    const currentRequest = request.current;
    setLocating(true);
    const fail = (reason) => {
      if (request.current !== currentRequest) return;
      setLocating(false);
      setError(reason?.code === 1
        ? 'La localisation a été refusée. Vous pouvez choisir une ville.'
        : 'Impossible de vous localiser pour le moment. Réessayez ou choisissez une ville.');
    };
    try {
      navigator.geolocation.getCurrentPosition(({ coords }) => {
        if (request.current !== currentRequest) return;
        chooseArea({ kind: 'position', lat: Number(coords.latitude.toFixed(3)), lon: Number(coords.longitude.toFixed(3)) });
      }, fail, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    } catch (reason) {
      fail(reason);
    }
  }, [chooseArea]);

  const value = useMemo(() => ({
    ...preferences, cities, ready, storageAvailable, locating, error, chooseCity, clearArea, setRadius, locate,
  }), [preferences, cities, ready, storageAvailable, locating, error, chooseCity, clearArea, setRadius, locate]);
  return <EventLocationContext.Provider value={value}>{children}</EventLocationContext.Provider>;
};

export const useEventLocation = () => useContext(EventLocationContext);
